"use client";

import { c as _c } from "react/compiler-runtime";
import { LoginFormContent } from "@/components/login-dialog/form";
import { PostLoginChoiceDialog } from "@/components/post-login-choice-dialog";
import { Button } from "@/components/rac/button";
import { DialogContent, DialogOverlay, DialogTrigger } from "@/components/rac/dialog";
import { Sheet, SheetContent, SheetOverlay } from "@/components/rac/sheet";
import { useLoginForm } from "@/hooks/use-login-form";
import { useMediaQuery } from "@/hooks/use-media-query";
import { X } from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";
import { createContext, useEffect, useState } from "react";
interface LoginDialogOptions {
  onLoginSuccess?: () => void;
  isShort?: boolean;
}
export interface LoginDialogContextValue {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  openDialog: (options?: LoginDialogOptions) => void;
  closeDialog: () => void;
}
export const LoginDialogContext = createContext<LoginDialogContextValue | undefined>(undefined);
export interface LoginDialogProviderProps {
  children: ReactNode;
}
export function LoginDialogProvider(t0) {
  const $ = _c(32);
  const {
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dialogOptions, setDialogOptions] = useState(t1);
  const isMobile = useMediaQuery("(max-width: 768px)");
  let t2;
  if ($[1] !== dialogOptions.isShort || $[2] !== dialogOptions.onLoginSuccess) {
    t2 = {
      onLoginSuccess: dialogOptions.onLoginSuccess,
      setIsOpen,
      isShort: dialogOptions.isShort
    };
    $[1] = dialogOptions.isShort;
    $[2] = dialogOptions.onLoginSuccess;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formProps = useLoginForm(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t4 => {
      const options = t4 === undefined ? {} : t4;
      setDialogOptions(options);
      setIsOpen(true);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const openDialog = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsOpen(false);
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const closeDialog = t4;
  let t5;
  let t6;
  if ($[6] !== dialogOptions.isShort || $[7] !== formProps || $[8] !== isOpen) {
    t5 = () => {
      if (!isOpen && !dialogOptions.isShort) {
        const timer = setTimeout(() => {
          formProps.resetForm();
        }, 300);
        return () => clearTimeout(timer);
      }
    };
    t6 = [isOpen, formProps, dialogOptions.isShort];
    $[6] = dialogOptions.isShort;
    $[7] = formProps;
    $[8] = isOpen;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== isOpen) {
    t7 = {
      isOpen,
      setIsOpen,
      openDialog,
      closeDialog
    };
    $[11] = isOpen;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const value = t7;
  let t8;
  if ($[13] !== dialogOptions.isShort || $[14] !== formProps || $[15] !== isMobile || $[16] !== isOpen) {
    t8 = () => {
      if (!isOpen) {
        return null;
      }
      if (dialogOptions.isShort) {
        return <LoginFormContent {...formProps} isMobile={isMobile} />;
      }
      if (isMobile) {
        return <Sheet isOpen={isOpen} onOpenChange={setIsOpen}><SheetOverlay className="z-10000!"><SheetContent side="bottom" className="h-auto max-h-[90dvh] rounded-t-2xl bg-gradient-to-br from-orange-100 via-red-50 to-pink-50" closeButton={false}><div className="relative"><Button variant="bareshell" onClick={() => setIsOpen(false)} className="absolute -top-14 right-4 p-2 z-1 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors" aria-label="Close dialog"><X className="h-6 w-6 text-gray-800" /></Button></div><div className="flex-1 overflow-y-auto"><LoginFormContent {...formProps} isMobile={true} /></div></SheetContent></SheetOverlay></Sheet>;
      }
      return <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}><DialogOverlay className="bg-black/50 z-10000!"><div className="relative max-w-4xl"><DialogContent className="max-w-4xl w-full h-[600px] md:h-[600px] sm:h-full sm:max-h-[90dvh] bg-transparent border-0 shadow-none p-0" closeButton={false}><Button variant="bareshell" onClick={() => setIsOpen(false)} className="absolute -right-4 -top-4 p-2 z-1 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors" aria-label="Close dialog"><X className="h-6 w-6 text-gray-800" /></Button><div className="p-0 overflow-hidden bg-white border-0 shadow-2xl sm:rounded-2xl rounded-none mb-8"><div className="flex flex-col md:flex-row h-full" data-testid="login-dialog" id="login-dialog"><div className="md:w-1/2 bg-gradient-to-br from-red-500 via-red-400 to-red-600 relative overflow-hidden"><div className="absolute inset-0 opacity-20"><div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent" /></div><div className="flex flex-col items-center justify-center h-full p-6 md:p-8"><div className="text-center max-w-sm"><div className="relative h-40 mb-6"><Image src="/images/login/unlock.svg" alt="Login illustration" fill={true} className="object-contain" /></div><h3 className="text-lg font-semibold text-white mb-2">Welcome to Settlin</h3><p className="text-sm text-white/90">Unlock your perfect home with our personalized property recommendations</p></div></div></div><div className="flex-1 bg-gradient-to-br from-orange-100 via-red-50 to-pink-50 flex flex-col justify-center px-8 py-8 relative"><LoginFormContent {...formProps} /></div></div></div></DialogContent></div></DialogOverlay></DialogTrigger>;
    };
    $[13] = dialogOptions.isShort;
    $[14] = formProps;
    $[15] = isMobile;
    $[16] = isOpen;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const renderDialog = t8;
  let t9;
  if ($[18] !== renderDialog) {
    t9 = renderDialog();
    $[18] = renderDialog;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== formProps) {
    t10 = () => formProps.setShowRelationshipGuard(false);
    $[20] = formProps;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = formProps.currentUser?.relation === "buyer" || formProps.currentUser?.relation === "seller" ? formProps.currentUser.relation : "buyer";
  let t12;
  if ($[22] !== formProps.onRelationshipSelected || $[23] !== formProps.showRelationshipGuard || $[24] !== t10 || $[25] !== t11) {
    t12 = <PostLoginChoiceDialog isOpen={formProps.showRelationshipGuard} onClose={t10} currentRelationship={t11} featureName="Choose Your Role" description="Tell us how you'd like to use Settlin today" onComplete={formProps.onRelationshipSelected} />;
    $[22] = formProps.onRelationshipSelected;
    $[23] = formProps.showRelationshipGuard;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== children || $[28] !== t12 || $[29] !== t9 || $[30] !== value) {
    t13 = <LoginDialogContext.Provider value={value}>{children}{t9}{t12}</LoginDialogContext.Provider>;
    $[27] = children;
    $[28] = t12;
    $[29] = t9;
    $[30] = value;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}